import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import styled, { css } from 'styled-components';
import {
  getMerchantUsers,
  searchApplications,
  useApplicationStore,
  UserContext,
  useUsersStore
} from '../../contexts';
import {
  ButtonContainer,
  InputStatuses,
  InputWrapper,
  PrimaryAltButton,
  SecondaryAltButton,
  ToggleButton
} from '../atoms';
import { ProductTypeSelect } from '../molecules';

type TApplicationsFilter = {
  filter?: object;
  nonce?: number;
};

type TFilter = {
  open: boolean;
};

const Filter = styled.form<TFilter>(
  ({ open }) => css`
    margin-top: 20px;
    max-height: 0;
    overflow: hidden;
    transition: 0.35s ease;

    ${open &&
    css`
      transition: 0.5s cubic-bezier(0.79, -0.06, 1, 1);
      max-height: 1000px;
    `}
  `
);

const InputContainer = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    align-items: end;
    grid-gap: 0 ${theme.margins.md};
    border-top: ${theme.borders.light};
    border-bottom: ${theme.borders.light};
    padding-top: ${theme.paddings.md};
    padding-bottom: ${theme.paddings.sm};

    @media (max-width: ${theme.breakpoints.max.md}) {
      grid-gap: 0 1.25rem;

      label {
        font-size: 0.875rem;
      }
    }
  `
);

const StatusesWrapper = styled.div`
  grid-column: 1 / span 4;
`;

const FormButtons = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 20px;
`;

const filterFormSchema = z.object({
  applicationNumber: z.string().optional(),
  applicationStep: z.array(z.string()).optional(),
  borrowerName: z.string().optional(),
  email: z.string().optional(),
  endDate: z.string().optional(),
  includeMigrated: z.boolean().optional(),
  productType: z.string().optional(),
  salesAgent: z.string().optional(),
  salesAgentEmail: z.string().optional(),
  salesAgentId: z.string().optional(),
  startDate: z.string().optional()
});

type FilterFormSchema = z.infer<typeof filterFormSchema>;

const ApplicationsFilter = ({
  nonce = 1,
  filter: initialFilter = {}
}: TApplicationsFilter) => {
  //#TODO rework width proper merchant id --------------------------
  // const merchantId = '321d3cf9-20c9-4f11-9e73-34c73c8aab52';
  // const merchantId = 'EP00001';
  // const merchantId = useLoggedInUserStore((store) => store.merchant);
  const { user, userInfo } = useContext(UserContext);
  const merchantId = user.merchantId;
  // TODO log
  // console.log('ApplicationsFilter', merchantId);
  // console.log('userContext:', JSON.stringify(user, null, 2));

  // Sales user doesn't have permission to get all applications
  if (userInfo.userId && userInfo.role === 'Sales') {
    initialFilter = {
      salesAgentId: userInfo.userId,
      salesAgentEmail: user.email
    };
  }

  const getDefaultValues = () => ({
    // Values don't 'reset' if undefined in react hook form
    applicationNumber: '',
    applicationStep: [],
    borrowerName: '',
    email: '',
    endDate: '',
    includeMigrated: false,
    productType: '',
    salesAgent: '{}',
    salesAgentEmail: '',
    salesAgentId: '',
    startDate: '',
    ...JSON.parse(JSON.stringify(initialFilter))
  });

  const translateFormData = (data = {}) => {
    const { applicationStep, ...searchFilter } = data;
    if (applicationStep && applicationStep.length) {
      searchFilter.applicationStep = Array.isArray(applicationStep)
        ? applicationStep.join(',')
        : applicationStep;
    }
    // reset sales agent details
    let { id = '', email = '' } = searchFilter.salesAgent
      ? JSON.parse(searchFilter.salesAgent)
      : {};
    if (userInfo.userId && userInfo.role === 'Sales') {
      id = userInfo.userId;
      email = user.email;
    }
    searchFilter.salesAgentId = id;
    searchFilter.salesAgentEmail = email;
    return searchFilter;
  };

  const [filter, setFilter] = useState(initialFilter);
  const [error, setError] = useState(undefined);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors }
  } = useForm<FilterFormSchema>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: getDefaultValues()
  });

  const clearState = useUsersStore((s) => s.clearState);
  const setApplications = useApplicationStore((s) => s.setApplications);
  const setMerchantUsers = useUsersStore((s) => s.setMerchantUsers);
  const loading = useApplicationStore((s) => s.isLoading);
  const setLoading = useApplicationStore((s) => s.setLoading);
  const merchantUsers = useUsersStore((s) => s.merchantUsers);

  // @TODO - this should be restricted on the backend
  // Sales user doesn't have permission to get all users
  const { isLoading, refetch } = getMerchantUsers({
    merchantId,
    setMerchantUsers,
    query: useQuery,
    enabled: userInfo.role !== 'Sales' // used to avoid react white screen hook error
  });

  useEffect(() => {
    return () => clearState();
  }, [clearState]);

  searchApplications({
    merchantId,
    filter,
    nonce,
    setLoading,
    query: useQuery,
    setApplications
  });

  const onSubmit = async (data: any) => {
    if (!loading) {
      const searchFilter = translateFormData(data);
      setFilter(searchFilter);
    }
  };

  const handleReset = () => {
    if (!loading) {
      reset();
      const defaults = getDefaultValues();
      setFilter(translateFormData(defaults));
      // toggle to trigger explicit render (where required)
      setResetForm(!resetForm);
    }
  };

  return (
    <div>
      <ToggleButton
        alternative={isFilterOpen}
        onClick={() => setIsFilterOpen(!isFilterOpen)}
      >
        {isFilterOpen ? 'Close' : 'Open'} Filters
      </ToggleButton>
      <Filter
        open={isFilterOpen}
        onSubmit={handleSubmit(onSubmit)}
        onReset={handleReset}
      >
        <InputContainer>
          <InputWrapper
            label="Application Number"
            name="applicationNumber"
            type="text"
            errorMessage={undefined}
            register={register}
          />
          <InputWrapper
            label="Borrower or Student Name"
            name="borrowerName"
            type="text"
            errorMessage={undefined}
            register={register}
          />
          <InputWrapper
            label="Email"
            name="email"
            type="text"
            errorMessage={undefined}
            register={register}
          />
          {userInfo.role !== 'Sales' && (
            <InputWrapper
              label="Sales Agent"
              name="salesAgent"
              type="select"
              complexOptions={merchantUsers?.map(({ id, name, email }) => ({
                value: JSON.stringify({ id, email }),
                text: name
              }))}
              errorMessage={undefined}
              register={register}
            />
          )}
          <ProductTypeSelect register={register} merchantId={merchantId} />
          <InputWrapper
            label="Start Date"
            name="startDate"
            type="date"
            errorMessage={undefined}
            register={register}
          />
          <InputWrapper
            label="End Date"
            name="endDate"
            type="date"
            errorMessage={undefined}
            register={register}
          />
          <StatusesWrapper>
            <InputStatuses
              name="applicationStep"
              type="application"
              reset={resetForm}
              state={getValues('applicationStep')}
              register={register}
            />
          </StatusesWrapper>
        </InputContainer>
        <FormButtons
          horizontalSpacing="6px"
          verticalSpacing="0"
          justify="flex-end"
        >
          <PrimaryAltButton type="submit" isLoading={loading}>
            Apply Filters
          </PrimaryAltButton>
          <SecondaryAltButton type="reset" isLoading={loading}>
            Reset Filters
          </SecondaryAltButton>
        </FormButtons>
      </Filter>
    </div>
  );
};

export { ApplicationsFilter };
